// src/pages/GuestPages/PaymentPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePayment } from '../../contexts/PaymentContext';
import api from '../../services/api';
import './PaymentPage.css';

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentData, initializePayment, setProvider, setPhone, updatePaymentStatus, setError } = usePayment();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [pollingAttempts, setPollingAttempts] = useState(0); // ✅ Nouveau : compteur de tentatives

  // Récupérer le type de ticket depuis la navigation
  const ticketType = location.state?.ticketType || 'gold';
  const amount = ticketType === 'vip' ? 50000 : 1500;

  // ✅ PARAMÈTRES DE POLLING AUGMENTÉS
  const POLLING_INTERVAL = 3000;      // 3 secondes entre chaque vérification
  const MAX_POLLING_TIME = 180000;    // 3 minutes total (180 secondes) au lieu de 5 min
  const MAX_ATTEMPTS = Math.floor(MAX_POLLING_TIME / POLLING_INTERVAL); // 60 tentatives

  useEffect(() => {
    // Initialiser le paiement au chargement de la page
    initializePayment(ticketType, amount);
  }, [ticketType, amount, initializePayment]);

  // Gestion du countdown pour le paiement
  useEffect(() => {
    let timer;
    if (countdown !== null && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setError('Délai de paiement expiré. Si vous avez payé, vous pouvez récupérer votre ticket plus tard.');
      setIsProcessing(false);
      setCountdown(null);
    }
    return () => clearTimeout(timer);
  }, [countdown, setError]);

  const handleProviderSelect = (selectedProvider) => {
    setProvider(selectedProvider);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Garder seulement les chiffres
    if (value.length <= 9) {
      setPhone(value);
    }
  };

  const handlePayment = async () => {
    if (!paymentData.provider || !paymentData.phone) {
      setError('Veuillez sélectionner un opérateur et entrer votre numéro');
      return;
    }

    // Validation du numéro
    const phoneRegex = /^6[5-9]\d{7}$/;
    if (!phoneRegex.test(paymentData.phone)) {
      setError('Numéro de téléphone invalide. Format: 6XXXXXXXX');
      return;
    }

    setIsProcessing(true);
    setPollingAttempts(0); // ✅ Reset compteur
    updatePaymentStatus('processing');

    try {
      // Appel à l'API pour initier le paiement
      const response = await api.post('/payment/initiate', {
        provider: paymentData.provider,
        phone: paymentData.phone,
        amount: paymentData.amount,
        ticket_type: ticketType
      });

      if (response.data.success) {
        const { payment_id, transaction_id, message } = response.data.data;

        updatePaymentStatus('pending', {
          paymentId: payment_id,
          transactionId: transaction_id
        });

        // ✅ Démarrer le countdown (3 minutes au lieu de 5)
        setCountdown(MAX_POLLING_TIME / 1000);

        // Afficher les instructions à l'utilisateur
        const instructions = paymentData.provider === 'orange' 
          ? '#150*50# pour Orange Money'
          : '*126# pour MTN Mobile Money';
        
        alert(message || `Paiement initié. Composez ${instructions} pour confirmer le paiement de ${amount} FCFA.`);

        // ✅ Vérifier le statut du paiement avec le nouveau système
        let attempts = 0;
        const checkInterval = setInterval(async () => {
          attempts++;
          setPollingAttempts(attempts);

          try {
            // ✅ Utiliser transaction_id au lieu de payment_id
            const statusResponse = await api.get(`/payment/status/${transaction_id}`);
            
            if (statusResponse.data.success) {
              const status = statusResponse.data.data.status;

              if (status === 'success') {
                clearInterval(checkInterval);
                updatePaymentStatus('success', {
                  paymentId: payment_id,
                  transactionId: transaction_id
                });
                setCountdown(null);
                setIsProcessing(false);
                
                // Rediriger vers la page d'inscription
                navigate('/register', {
                  state: {
                    ticketType,
                    paymentId: transaction_id, // ✅ Utiliser transaction_id
                    amount: amount,
                    phone: paymentData.phone
                  }
                });
              } else if (status === 'failed') {
                clearInterval(checkInterval);
                setError('Le paiement a échoué. Veuillez réessayer.');
                setIsProcessing(false);
                setCountdown(null);
              }
            }

            // ✅ Arrêter si nombre max de tentatives atteint
            if (attempts >= MAX_ATTEMPTS) {
              clearInterval(checkInterval);
              setError(
                'Le délai d\'attente est dépassé, mais votre paiement peut toujours être en cours. ' +
                'Si vous avez payé, vous pourrez récupérer votre ticket plus tard.'
              );
              setIsProcessing(false);
              setCountdown(null);
            }

          } catch (error) {
            console.error('Erreur lors de la vérification du statut:', error);
            // Ne pas arrêter le polling sur une erreur réseau temporaire
          }
        }, POLLING_INTERVAL);

      } else {
        throw new Error(response.data.message || 'Erreur lors de l\'initiation du paiement');
      }
    } catch (error) {
      console.error('Erreur paiement:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors du paiement';
      setError(errorMessage);
      setIsProcessing(false);
      setCountdown(null);
    }
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isPhoneValid = /^6[5-9]\d{7}$/.test(paymentData.phone);
  const canPay = paymentData.provider && isPhoneValid && !isProcessing;

  return (
    <div className="payment-page">
      <div className="payment-container">
        <button className="back-button" onClick={() => navigate(-1)} disabled={isProcessing}>
          ← Retour
        </button>

        <div className="payment-header">
          <h1>Paiement</h1>
          <div className="payment-amount">
            <span className="amount-label">Montant à payer:</span>
            <span className="amount-value">{amount.toLocaleString()} FCFA</span>
          </div>
          <div className="ticket-type-badge">
            {ticketType === 'vip' ? '👑 Ticket VIP' : '🎟️ Ticket GOLD'}
          </div>
        </div>

        {paymentData.error && (
          <div className="error-message">
            ⚠️ {paymentData.error}
          </div>
        )}

        {/* ✅ INDICATEUR D'ATTENTE AMÉLIORÉ */}
        {isProcessing && countdown !== null && (
          <div className="payment-waiting">
            <div className="waiting-spinner">
              <div className="spinner"></div>
            </div>
            
            <h3>Validation du paiement en cours...</h3>
            
            <div className="waiting-info">
              <p className="main-message">
                📱 <strong>Vérifiez votre téléphone</strong> et entrez votre code PIN{' '}
                {paymentData.provider === 'orange' ? <strong>Orange Money</strong> : <strong>MTN Mobile Money</strong>}
              </p>
              
              <div className="timing-info">
                <div className="time-box">
                  <span className="time-label">Temps écoulé</span>
                  <span className="time-value">
                    {formatCountdown(Math.floor((pollingAttempts * POLLING_INTERVAL) / 1000))}
                  </span>
                </div>
                <div className="time-box">
                  <span className="time-label">Temps restant</span>
                  <span className="time-value time-remaining">{formatCountdown(countdown)}</span>
                </div>
              </div>
              
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${Math.min((pollingAttempts / MAX_ATTEMPTS) * 100, 100)}%` }}
                ></div>
              </div>
              
              <div className="waiting-tips">
                <h4>💡 Conseils importants :</h4>
                <ul>
                  <li>Gardez votre téléphone à portée de main</li>
                  <li>Ne fermez pas cette page pendant la validation</li>
                  <li>Le processus peut prendre jusqu'à 3 minutes</li>
                  <li>
                    {paymentData.provider === 'orange' ? (
                      <>Si votre connexion est coupée par Orange Money, ne vous inquiétez pas : vous pourrez récupérer votre ticket plus tard</>
                    ) : (
                      <>Assurez-vous d'avoir une bonne connexion Internet</>
                    )}
                  </li>
                </ul>
              </div>
              
              {pollingAttempts > MAX_ATTEMPTS * 0.8 && (
                <div className="warning-message">
                  ⏰ Le délai approche de la fin. Si vous n'avez pas encore reçu l'USSD, 
                  vérifiez votre téléphone ou réessayez le paiement.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ÉTAPE 1: Sélection du provider */}
        {!isProcessing && (
          <div className="payment-section">
            <h2>1. Choisissez votre opérateur</h2>
            <div className="payment-providers">
              <button
                className={`provider-button ${paymentData.provider === 'mtn' ? 'selected' : ''}`}
                onClick={() => handleProviderSelect('mtn')}
                disabled={isProcessing}
              >
                <div className="provider-logo mtn-logo">MTN</div>
                <div className="provider-name">MTN Mobile Money</div>
              </button>

              <button
                className={`provider-button ${paymentData.provider === 'orange' ? 'selected' : ''}`}
                onClick={() => handleProviderSelect('orange')}
                disabled={isProcessing}
              >
                <div className="provider-logo orange-logo">OM</div>
                <div className="provider-name">Orange Money</div>
              </button>
            </div>
          </div>
        )}

        {/* ÉTAPE 2: Saisie du numéro (affiché après sélection provider) */}
        {paymentData.provider && !isProcessing && (
          <div className="payment-section">
            <h2>2. Entrez votre numéro</h2>
            <div className="phone-input-wrapper">
              <div className="phone-prefix">+237</div>
              <input
                type="tel"
                className="phone-input"
                placeholder="6XXXXXXXX"
                value={paymentData.phone}
                onChange={handlePhoneChange}
                maxLength={9}
                disabled={isProcessing}
              />
            </div>
            {paymentData.phone && !isPhoneValid && (
              <div className="input-hint error">
                Le numéro doit commencer par 65, 66, 67, 68 ou 69
              </div>
            )}
            {isPhoneValid && (
              <div className="input-hint success">
                ✓ Numéro valide
              </div>
            )}
          </div>
        )}

        {/* ÉTAPE 3: Bouton de paiement (actif si conditions OK) */}
        {paymentData.provider && !isProcessing && (
          <div className="payment-section">
            <h2>3. Confirmez le paiement</h2>
            <button
              className="pay-button"
              onClick={handlePayment}
              disabled={!canPay}
            >
              {isProcessing ? (
                <>
                  <span className="spinner"></span>
                  En cours...
                </>
              ) : (
                `Payer ${amount.toLocaleString()} FCFA`
              )}
            </button>

            <div className="payment-info">
              <p>
                Après avoir cliqué sur "Payer", vous recevrez une notification sur votre téléphone.
              </p>
              <p>
                Composez <strong>*126#</strong> (MTN) ou <strong>#150*50#</strong> (Orange) pour confirmer le paiement.
              </p>
            </div>
          </div>
        )}

        {/* Bon d'achat info */}
        {!isProcessing && (
          <div className="voucher-info">
            <h3>🎁 Bon d'achat inclus</h3>
            <p>
              Votre ticket inclut un bon d'achat de <strong>{amount.toLocaleString()} FCFA</strong> 
              {ticketType === 'vip' ? ' valable sur tous les stands VIP' : ' valable dans tous les stands'}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;